import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { config } from '../../../config';
import { ErrorService } from '../../services/error.service';
import { CVUploadComponent } from '../cv-upload/cv-upload.component';

interface Contact {
  id?: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  company_id: string;
  job_title?: string;
  notes?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  cv_file_path?: string;
  cv_file_name?: string;
  cv_file_size?: number;
  cv_uploaded_at?: string;
}

interface CVFile {
  name: string;
  size: number;
  uploadedAt: Date;
  uploadedBy?: string;
}

interface Company {
  id: string;
  name: string;
  description?: string;
  status?: string;
}

@Component({
  selector: 'app-contact-edit',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CVUploadComponent],
  template: `
    <div class="contact-edit-modal" *ngIf="isVisible">
      <div class="modal-backdrop" (click)="onCancel()"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ contact?.id ? 'Edit Contact' : 'Add New Contact' }}</h3>
          <button class="close-btn" (click)="onCancel()" type="button">&times;</button>
        </div>
        
        <div class="modal-body">
          <form [formGroup]="contactForm" (ngSubmit)="onSave()">
            <div class="form-group" *ngIf="showCompanyField">
              <label for="company_id">Company <span class="required">*</span></label>
              <select id="company_id" formControlName="company_id" [disabled]="!showCompanyField">
                <option value="">Select Company</option>
                <option *ngFor="let company of companies" [value]="company.id">
                  {{ company.name }}
                </option>
              </select>
              <div *ngIf="contactForm.get('company_id')?.invalid && contactForm.get('company_id')?.touched" class="error-message">
                Company is required
              </div>
            </div>
            
            <div class="form-group">
              <label for="first_name">First Name <span class="required">*</span></label>
              <input type="text" id="first_name" formControlName="first_name" placeholder="Enter first name">
              <div *ngIf="contactForm.get('first_name')?.invalid && contactForm.get('first_name')?.touched" class="error-message">
                First name is required
              </div>
            </div>
            
            <div class="form-group">
              <label for="last_name">Last Name <span class="required">*</span></label>
              <input type="text" id="last_name" formControlName="last_name" placeholder="Enter last name">
              <div *ngIf="contactForm.get('last_name')?.invalid && contactForm.get('last_name')?.touched" class="error-message">
                Last name is required
              </div>
            </div>
            
            <div class="form-group">
              <label for="email">Email <span class="required">*</span></label>
              <input type="email" id="email" formControlName="email" placeholder="Enter email address">
              <div *ngIf="contactForm.get('email')?.invalid && contactForm.get('email')?.touched" class="error-message">
                <div *ngIf="contactForm.get('email')?.errors?.['required']">Email is required</div>
                <div *ngIf="contactForm.get('email')?.errors?.['email']">Please enter a valid email address</div>
              </div>
            </div>
            
            <div class="form-group">
              <label for="mobile">Mobile Number <span class="required">*</span></label>
              <input type="tel" id="mobile" formControlName="mobile" placeholder="+441234567890">
              <div *ngIf="contactForm.get('mobile')?.invalid && contactForm.get('mobile')?.touched" class="error-message">
                <span *ngIf="contactForm.get('mobile')?.errors?.['required']">Mobile number is required</span>
                <span *ngIf="contactForm.get('mobile')?.errors?.['pattern']">Use international format with country code, no spaces (e.g. +441234567890)</span>
              </div>
            </div>
            
            <div class="form-group">
              <label for="job_title">Job Title <span class="optional">(Optional)</span></label>
              <input type="text" id="job_title" formControlName="job_title" placeholder="Enter job title">
            </div>
            
            <!-- CV Upload Section - positioned before Notes -->
            <app-cv-upload #cvUpload
              [contactId]="contact?.id || null"
              [existingCvFile]="existingCvFile"
              [required]="false"
              (cvUploaded)="onCVUploaded($event)"
              (cvDeleted)="onCVDeleted()"
              (fileSelected)="onCVFileSelected($event)"
              (validationChange)="onCVValidationChange($event)">
            </app-cv-upload>
            
            <div class="form-group">
              <label for="notes">Notes <span class="optional">(Optional)</span></label>
              <textarea id="notes" formControlName="notes" placeholder="Enter notes" rows="3"></textarea>
            </div>
            
            <div class="form-actions">
              <button type="submit" [disabled]="contactForm.invalid || saving" class="save-btn">
                {{ saving ? 'Saving...' : (contact?.id ? 'Update' : 'Create') }}
              </button>
              <button type="button" class="cancel-btn" (click)="onCancel()">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .contact-edit-modal {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-backdrop {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(13,18,30,.45);
    }

    .modal-content {
      background: var(--ap-surface, #fff);
      border-radius: var(--ap-radius-lg, 14px);
      width: 500px;
      max-width: 90vw;
      max-height: 90vh;
      overflow-y: auto;
      border: 1px solid var(--ap-border, #E3E6EB);
      position: relative;
      z-index: 2001;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
    }

    .modal-header h3 {
      margin: 0;
      color: var(--ap-text, #0B1220);
      font-size: 15px;
      font-weight: 600;
    }

    .close-btn {
      background: none;
      border: none;
      width: 28px;
      height: 28px;
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 18px;
      cursor: pointer;
      color: var(--ap-text-muted, #6B7280);
      padding: 0;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s;
    }

    .close-btn:hover {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text, #0B1220);
    }

    .modal-body {
      padding: 16px 20px 20px;
    }

    .form-group {
      margin-bottom: 14px;
    }

    label {
      display: block;
      margin-bottom: 5px;
      font-weight: 550;
      font-size: 12px;
      color: var(--ap-text-soft, #4B5563);
    }

    .required {
      color: var(--ap-danger, #B42318);
      margin-left: 2px;
    }

    input, select, textarea {
      width: 100%;
      padding: 7px 10px;
      border-radius: var(--ap-radius-sm, 6px);
      border: 1px solid var(--ap-border, #E3E6EB);
      font-size: 13px;
      font-family: inherit;
      box-sizing: border-box;
      background: var(--ap-surface, #fff);
      color: var(--ap-text, #0B1220);
    }

    input:focus, select:focus, textarea:focus {
      outline: none;
      border-color: var(--ap-primary, #2F5BDA);
      box-shadow: var(--ap-ring, 0 0 0 3px rgba(47,91,218,.18));
    }

    textarea {
      resize: vertical;
      min-height: 60px;
      line-height: 1.5;
    }

    .error-message {
      color: var(--ap-danger, #B42318);
      font-size: 11px;
      margin-top: 4px;
    }

    .form-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 16px;
      padding-top: 14px;
      border-top: 1px solid var(--ap-border, #E3E6EB);
    }

    .save-btn, .cancel-btn {
      padding: 0 14px;
      height: 32px;
      border: 1px solid transparent;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 13px;
      font-weight: 550;
      min-width: 72px;
      transition: background 0.15s;
    }

    .save-btn {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border-color: var(--ap-primary, #2F5BDA);
    }

    .save-btn:hover:not(:disabled) {
      background: var(--ap-primary-ink, #1E3FA8);
      border-color: var(--ap-primary-ink, #1E3FA8);
    }

    .save-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .cancel-btn {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text-soft, #4B5563);
      border-color: var(--ap-border, #E3E6EB);
    }

    .cancel-btn:hover {
      background: var(--ap-border, #E3E6EB);
    }

    .optional {
      color: var(--ap-text-muted, #6B7280);
      margin-left: 4px;
      font-size: 12px;
      font-weight: normal;
    }

    .field-hint {
      color: var(--ap-text-muted, #6B7280);
      font-size: 11px;
      margin-top: 4px;
    }
  `]
})
export class ContactEditComponent implements OnInit, OnChanges {
  @Input() isVisible: boolean = false;
  @Input() contact: Contact | null = null;
  @Input() companies: Company[] = [];
  @Input() showCompanyField: boolean = true;
  @Input() fixedCompanyId?: string; // For when company is pre-determined
  
  @Output() saveContact = new EventEmitter<Contact>();
  @Output() cancelEdit = new EventEmitter<void>();
  @Output() contactSaved = new EventEmitter<Contact>();
  
  @ViewChild('cvUpload') cvUploadComponent!: CVUploadComponent;
  
  contactForm: FormGroup;
  saving: boolean = false;
  existingCvFile: CVFile | null = null;
  selectedCVFile: File | null = null;
  cvValidationError: string | null = null;
  
  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private errorService: ErrorService
  ) {
    this.contactForm = this.fb.group({
      id: [''],
      first_name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
      last_name: ['', [Validators.required, Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.email]],
      mobile: ['', [Validators.required, Validators.pattern(/^\+[0-9]{11,14}$/)]],
      company_id: ['', Validators.required],
      job_title: ['', Validators.maxLength(100)],
      notes: ['', Validators.maxLength(500)]
    });
  }
  
  ngOnInit(): void {
    this.loadCompanies();
    this.setupForm();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contact'] || changes['isVisible']) {
      this.setupForm();
      this.setupExistingCV();
    }
    
    if (changes['fixedCompanyId'] && this.fixedCompanyId) {
      this.contactForm.patchValue({ company_id: this.fixedCompanyId });
      if (!this.showCompanyField) {
        this.contactForm.get('company_id')?.disable();
      }
    }
  }
  
  private setupForm(): void {
    if (this.contact) {
      this.contactForm.patchValue({
        id: this.contact.id || '',
        first_name: this.contact.first_name,
        last_name: this.contact.last_name || '',
        email: this.contact.email || '',
        mobile: this.contact.mobile || '',
        company_id: this.contact.company_id,
        job_title: this.contact.job_title || '',
        notes: this.contact.notes || ''
      });
    } else {
      this.contactForm.reset();
      if (this.fixedCompanyId) {
        this.contactForm.patchValue({ company_id: this.fixedCompanyId });
      }
    }
    
    // Handle company field visibility
    if (!this.showCompanyField && this.fixedCompanyId) {
      this.contactForm.get('company_id')?.disable();
    }
  }
  
  private loadCompanies(): void {
    if (this.companies.length > 0) return; // Already loaded
    
    this.http.get<any>(`${config.apiUrl}/api/companies`)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.companies = response.data;
          }
        },
        error: (err) => {
          console.error('Failed to load companies:', err);
        }
      });
  }
  
  onSave(): void {
    // Check CV validation
    const cvError = this.getCVValidationError();
    if (cvError) {
      this.errorService.showError(cvError);
      return;
    }
    
    if (this.contactForm.invalid || this.saving) return;
    
    this.saving = true;
    
    // Get the raw value to include disabled fields
    const formValue = this.contactForm.getRawValue();
    
    const contactData = {
      first_name: formValue.first_name,
      last_name: formValue.last_name,
      email: formValue.email,
      mobile: formValue.mobile,
      company_id: formValue.company_id,
      job_title: formValue.job_title || null,
      notes: formValue.notes || null
    };
    
    let request;
    
    if (this.contact?.id) {
      // Update existing contact
      request = this.http.put<any>(`${config.apiUrl}/api/contacts/${this.contact.id}`, contactData);
    } else {
      // Create new contact
      request = this.http.post<any>(`${config.apiUrl}/api/contacts`, contactData);
    }
    
    request.subscribe({
      next: async (response) => {
        if (response.success) {
          const savedContact = response.data || { ...contactData, id: response.id };
          
          // Handle CV upload for new contact
          if (!this.contact?.id && this.selectedCVFile) {
            try {
              await this.uploadCVForNewContact(savedContact.id || response.data);
            } catch (cvError) {
              console.error('CV upload failed for new contact:', cvError);
              // Don't fail the contact creation, just show warning
              this.errorService.showError('Contact created but CV upload failed. You can upload it later.');
            }
          }
          
          this.contactSaved.emit(savedContact);
          this.errorService.showInfo('Contact saved successfully', true, 3000);
          this.resetAndClose();
        } else {
          this.errorService.showError(response.message || 'Failed to save contact');
        }
        this.saving = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err, 'Failed to save contact');
        this.saving = false;
      }
    });
  }
  
  onCancel(): void {
    this.resetAndClose();
  }
  
  private resetAndClose(): void {
    this.contactForm.reset();
    this.saving = false;
    this.existingCvFile = null;
    this.selectedCVFile = null;
    this.cvValidationError = null;
    this.cancelEdit.emit();
  }

  // Validate mobile input and show error if invalid characters are entered
  validateMobileInput(event: any): void {
    const value = event.target.value;
    const mobilePattern = /^[+]?[0-9\s\-\(\)]*$/;
    
    const mobileControl = this.contactForm.get('mobile');
    if (mobileControl) {
      if (value && !mobilePattern.test(value)) {
        mobileControl.setErrors({ pattern: true });
      } else if (mobileControl.errors?.['pattern']) {
        // Remove pattern error if input is now valid
        const errors = { ...mobileControl.errors };
        delete errors['pattern'];
        const hasOtherErrors = Object.keys(errors).length > 0;
        mobileControl.setErrors(hasOtherErrors ? errors : null);
      }
    }
  }

  // CV-related methods
  private setupExistingCV(): void {
    if (this.contact && (this.contact.cv_file_name || this.contact.cv_file_path)) {
      this.existingCvFile = {
        name: this.contact.cv_file_name!,
        size: this.contact.cv_file_size || 0,
        uploadedAt: new Date(this.contact.cv_uploaded_at || new Date())
      };
    } else {
      this.existingCvFile = null;
    }
  }

  onCVFileSelected(file: File): void {
    this.selectedCVFile = file;
  }

  onCVUploaded(cvFile: CVFile): void {
    this.existingCvFile = cvFile;
    this.selectedCVFile = null;
    this.cvValidationError = null;
  }

  onCVDeleted(): void {
    this.existingCvFile = null;
    this.selectedCVFile = null;
    this.cvValidationError = null;
  }

  onCVValidationChange(error: string | null): void {
    this.cvValidationError = error;
  }

  private getCVValidationError(): string | null {
    // CV is optional - only return actual validation errors
    return this.cvValidationError;
  }

  private async uploadCVForNewContact(contactId: string): Promise<void> {
    if (!this.selectedCVFile) return;

    const formData = new FormData();
    formData.append('cv', this.selectedCVFile);

    const response = await this.http.post<any>(
      `${config.apiUrl}/api/contacts/${contactId}/cv`,
      formData
    ).toPromise();

    if (!response.success) {
      throw new Error(response.message || 'Failed to upload CV');
    }
  }
}